import { useRef, useEffect } from "react";

type Dot = {
  radius: number;
  ang: number;
  speed: number;
  fillColor: string;
};

type ParticleCircleProps = {
  variant?: "gray" | "red";
  size?: "sm" | "lg";
};
export default function ParticleCircle({
  variant = "gray",
  size = "lg",
}: ParticleCircleProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (!canvasRef.current) return;

    const canvas = canvasRef.current;
    const context = canvas.getContext("2d");
    let numDots = size === "lg" ? 17200 : 8100,
      n = numDots,
      currDot: Dot,
      maxRad = size === "lg" ? 200 : 120,
      minRad = size === "lg" ? 120 : 60,
      radDiff = maxRad - minRad,
      dots: Dot[] = [],
      PI = Math.PI,
      centerPt = { x: 0, y: 0 };

    resizeHandler();

    while (n--) {
      currDot = {
        radius: minRad + Math.random() * radDiff,
        ang: (1 - Math.random() * 1.5) * PI,
        speed: (1 - Math.random() * 3) * 0.005,
        fillColor:
          variant === "red"
            ? `rgba(255, 0, 0, ${Math.random()})`
            : `rgba(0, 0, 0, ${Math.random()})`,
      };
      dots.push(currDot);
    }
    drawPoints();

    function drawPoints() {
      n = numDots;
      let _centerPt = centerPt,
        _context = context,
        dX = 0,
        dY = 0;

      _context.clearRect(0, 0, canvas.width, canvas.height);

      while (n--) {
        currDot = dots[n];
        dX = _centerPt.x + Math.sin(currDot.ang) * currDot.radius;
        dY = _centerPt.y + Math.cos(currDot.ang) * currDot.radius;

        currDot.ang += currDot.speed;

        _context.fillStyle = currDot.fillColor;
        _context.fillRect(dX, dY, 1, 1);
      }
      window.requestAnimationFrame(drawPoints);
    }

    function resizeHandler() {
      const box = canvas.getBoundingClientRect();
      const w = box.width;
      const h = box.height;
      canvas.width = w;
      canvas.height = h;
      centerPt.x = Math.round(w / 2);
      centerPt.y = Math.round(h / 2);
    }

    window.addEventListener("resize", resizeHandler);
    return () => {
      window.removeEventListener("resize", resizeHandler);
    };
  }, [canvasRef.current, variant, size]);

  return <canvas ref={canvasRef} className="aspect-square w-full" />;
}
